import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { BaseFieldComponent } from '../base-field.component';

@Component({
  selector: 'app-date-field',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule],
  template: `
    <div class="field-wrapper" [class]="field.cssClass" [class.has-error]="isInvalid">

      <label [for]="field.id">
        {{ field.label }}<a *ngIf="hasLabelLink" [href]="field.linkUrl" [target]="field.linkTarget ?? '_blank'" [attr.rel]="(field.linkTarget ?? '_blank') === '_blank' ? 'noopener noreferrer' : null">{{ field.linkText }}</a>
        <span *ngIf="hasRequired" class="required-mark">*</span>
      </label>

      <input
        [id]="field.id"
        type="date"
        [formControl]="control"
        [attr.min]="resolvedMin || null"
        [attr.max]="resolvedMax || null"
        [attr.aria-describedby]="field.hint ? field.id + '-hint' : null"
        (change)="onNativeChange($event)"
      />

      <small *ngIf="field.hint && !isInvalid"
             [id]="field.id + '-hint'">
        {{ field.hint }}
      </small>

      <small *ngIf="isInvalid" class="error">
        {{ errorMessage }}
      </small>

    </div>
  `
})
export class DateFieldComponent extends BaseFieldComponent implements OnInit, AfterViewInit, OnDestroy {

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    // Suscribirse a cambios programáticos del control (ej: edición con datos previos)
    if (this.isBirthDate && this.ageTargetFieldId) {
      this.control.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => this.fillAgeField(value));
    }
  }

  ngAfterViewInit(): void {
    // Si el formulario ya tiene un valor al inicializar (modo edición), calcular edad
    if (this.isBirthDate && this.ageTargetFieldId && this.control.value) {
      this.fillAgeField(this.control.value);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Manejador del evento nativo change: garantizado que se ejecuta con todos los inputs listos */
  onNativeChange(event: Event): void {
    if (!this.isBirthDate || !this.ageTargetFieldId) return;
    const input = event.target as HTMLInputElement;
    this.fillAgeField(input.value);
  }

  get resolvedMin(): string {
    const props = (this.field?.customProps as Record<string, unknown>) ?? {};
    return this.resolveDate(props['minDate'] as string | undefined);
  }

  get resolvedMax(): string {
    const props = (this.field?.customProps as Record<string, unknown>) ?? {};
    return this.resolveDate(props['maxDate'] as string | undefined);
  }

  get isBirthDate(): boolean {
    const props = (this.field?.customProps as Record<string, unknown>) ?? {};
    return !!props['isBirthDate'];
  }

  get ageTargetFieldId(): string {
    const props = (this.field?.customProps as Record<string, unknown>) ?? {};
    return (props['ageTargetFieldId'] as string) ?? '';
  }

  private fillAgeField(dateValue: string): void {
    if (!dateValue || !this.ageTargetFieldId || !this.form) return;
    const age = this.calculateAge(dateValue);
    const targetControl = this.form.get(this.ageTargetFieldId);
    if (targetControl) {
      targetControl.setValue(age, { emitEvent: true });
    }
  }

  private calculateAge(dateString: string): number {
    const today = new Date();
    const birth = new Date(dateString);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age < 0 ? 0 : age;
  }

  private resolveDate(value: string | undefined): string {
    if (!value) return '';
    if (value === 'today') return this.todayISO();
    return value;
  }

  private todayISO(): string {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  get hasRequired(): boolean {
    return !!this.field.validations?.some(v => v.type === 'required');
  }
}

